import { afterNextRender, Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';

import { SplitterBarComponent } from '../../../../../kendo/kendo-angular-layout/splitter/splitter-bar.component';
import { TiSplitterService } from './ti.splitter.service';

import { TiDraggableDirective } from '../common/ti.draggable.directive';
import { LocalizationService } from '@progress/kendo-angular-l10n';
import { CollapseDirection, TiSplitterPaneComponent } from './ti.splitter-pane.component';

const classFromObject = (classes) =>
  Object.keys(classes)
    .filter((c) => classes[c])
    .join(' ');

@Component({
  standalone: false,
  exportAs: 'tiKendoSplitterBar',
  selector: 'ti-kendo-splitter-bar, kendo-splitter-bar',
  template: `
    <div
      class="ti-splitbar-bar"
      [ngClass]="{
        'direction-left': this.collapseDirection === 'left',
        'direction-right': this.collapseDirection === 'right',
      }">
      <div class="top-splitter-wrapper">
        <div
          *ngIf="collapseDirection === 'left' || collapseDirection === 'top'"
          [class]="previousArrowClass()"
          (click)="togglePrevious()">
          <div class="k-splitbar-open-text">
            <ng-container i18n>Open</ng-container>
          </div>
          <div class="k-splitbar-hide-text">
            <ng-container i18n>Close</ng-container>
          </div>
        </div>

        <div
          *ngIf="collapseDirection === 'right' || collapseDirection === 'bottom'"
          [class]="nextArrowClass()"
          (click)="toggleNext()">
          <div class="k-splitbar-open-text">
            <ng-container i18n>Open</ng-container>
          </div>
          <div class="k-splitbar-hide-text">
            <ng-container i18n>Close</ng-container>
          </div>
        </div>

        <div
          class="splitter-bar-wrapper"
          [class.shortened-splitter-bar-wrapper]="collapseDirection"
          [class.direction-left]="collapseDirection === 'left'"
          [class.direction-right]="collapseDirection === 'right'"
          [class.direction-bottom]="collapseDirection === 'bottom'"
          [class.direction-top]="collapseDirection === 'top'">
          <div #tiSplitterBarLabel class="ti-splitter-bar-label">
            <i *ngIf="splitterBarIconClass" [class]="splitterBarIconClass"></i>
            {{ splitterBarLabel }}
          </div>
          <div class="ti-splitter-bar-resize-track" [ngStyle]="marginStyles">
            <div class="k-resize-handle"></div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class TiSplitterBarComponent extends SplitterBarComponent implements OnInit {
  @Input()
  public pane: TiSplitterPaneComponent;

  @Input()
  public containerIndex: string = 'default-index';

  @Input()
  public qaId: string = this.containerIndex;

  @ViewChild('tiSplitterBarLabel')
  public tiSplitterBarLabel: ElementRef<HTMLDivElement>;

  public splitterBarIconClass: string = '';

  public splitterBarLabel: string;

  public collapsed: boolean = false;

  public hidden: boolean = false;

  public collapseDirection: CollapseDirection;

  public marginStyles: Record<string, string | null> = {};

  ngOnInit() {
    super.ngOnInit();

    this.setDirectionAndCollapsedState();

    if (this.collapseDirection === 'left' || this.collapseDirection === 'top') {
      const pane = this.splitterService.pane(this.index);
      this.splitterBarIconClass = pane.splitterBarIconClass;
      this.splitterBarLabel = pane.splitterBarLabel;
      this.qaId = pane.splitterBarQAId;
      pane.collapseChangeRequred.subscribe((collapsed) => this.togglePane(this.index, collapsed));
    } else if (this.collapseDirection === 'right' || this.collapseDirection === 'bottom') {
      const pane = this.splitterService.pane(this.index + 1);
      pane.collapseChangeRequred.subscribe((collapsed) => this.togglePane(this.index + 1, collapsed));
      this.splitterBarIconClass = pane.splitterBarIconClass;
      this.splitterBarLabel = pane.splitterBarLabel;
      this.qaId = pane.splitterBarQAId;
    }
  }

  setDirectionAndCollapsedState() {
    const pane = this.splitterService.pane(this.index);
    const nextPane = this.splitterService.pane(this.index + 1);
    if (pane.collapsible) {
      this.collapseDirection = this.orientation === 'horizontal' ? 'left' : 'top';
      this.collapsed = pane.collapsed;
    } else if (nextPane.collapsible) {
      this.collapseDirection = this.orientation === 'horizontal' ? 'right' : 'bottom';
      this.collapsed = nextPane.collapsed;
    } else {
      this.collapseDirection = null;
    }
  }

  public isCollapsed(): boolean {
    return this.collapsed;
  }

  public togglePane(index, newValue?: boolean) {
    const pane = this.splitterService.pane(index);
    if (pane && pane.collapsible && !pane.collapsed) {
      this.splitterBarIconClass = pane.splitterBarIconClass;
      this.splitterBarLabel = pane.splitterBarLabel;
      this.qaId = pane.splitterBarQAId;
    }

    this.splitterService.tryToggle(index, newValue);
    this.collapsed = pane.collapsed;
    this.splitterService.fixStaticPanes();
  }

  public togglePrevious(newValue?: boolean): void {
    this.togglePane(this.index, newValue);
  }

  public toggleNext(newValue?: boolean): void {
    this.togglePane(this.index + 1, newValue);
  }

  public isCollapsible(): boolean {
    const pane = this.splitterService.pane(this.index);
    const nextPane = this.splitterService.pane(this.index + 1);
    return pane.collapsible || nextPane.collapsible;
  }

  public isHidden(): boolean {
    const pane = this.splitterService.pane(this.index);
    if (pane) {
      return pane.hiddenBar;
    }
    return false;
  }

  @HostBinding('attr.element-uid')
  public get refId(): string {
    // !!! test query: document.querySelectorAll('ti-kendo-splitter-bar[element-uid]')
    // get all splitter IDs: document.querySelectorAll('ti-kendo-splitter-bar[element-uid]').forEach((item) => { console.log(item.getAttribute('element-uid'))});
    return this.containerIndex;
  }

  @HostBinding('attr.qa-id')
  public get refQAId(): string {
    return this.qaId;
  }

  @HostBinding('class')
  public get hostClasses(): string {
    const isHorizontal = this.orientation === 'horizontal';
    const isDraggable = this.splitterService.isDraggable(this.index);
    const isStatic = this.splitterService.isStatic(this.index);
    const isCollapsed = this.isCollapsed();
    const isCollapsible = this.isCollapsible();
    const isHidden = this.isHidden();

    return classFromObject({
      'k-state-focused': this.focused,
      'k-splitbar': true,
      'k-splitbar-horizontal': isHorizontal,
      'k-splitbar-vertical': !isHorizontal,
      'k-splitbar-draggable-horizontal': isHorizontal && isDraggable,
      'k-splitbar-draggable-vertical': !isHorizontal && isDraggable,
      'k-splitbar-static-horizontal': isHorizontal && isStatic,
      'k-splitbar-static-vertical': !isHorizontal && isStatic,
      collapsed: isCollapsed,
      collapsible: isCollapsible,
      'k-splitbar-hidden': isHidden,
    });
  }

  public previousArrowClass(): string {
    const isHorizontal = this.orientation === 'horizontal';
    const isCollapsed = this.isCollapsed();

    return classFromObject({
      'k-font-icon': true,
      'k-collapse-prev': true,
      'k-i-arrow-60-left': isHorizontal && !isCollapsed,
      'k-i-arrow-60-right': isHorizontal && isCollapsed,
      'k-i-arrow-60-up': !isHorizontal && !isCollapsed,
      'k-i-arrow-60-down': !isHorizontal && isCollapsed,
      'top-arrow': true,
      'k-collapse-open': !isCollapsed,
      'k-collapse-hide': isCollapsed,
    });
  }

  public nextArrowClass(): string {
    const isHorizontal = this.orientation === 'horizontal';
    const isCollapsed = this.isCollapsed();

    return classFromObject({
      'k-font-icon': true,
      'k-collapse-next': true,
      'k-i-arrow-60-left': isHorizontal && isCollapsed,
      'k-i-arrow-60-right': isHorizontal && !isCollapsed,
      'k-i-arrow-60-up': !isHorizontal && isCollapsed,
      'k-i-arrow-60-down': !isHorizontal && !isCollapsed,
      'top-arrow': true,
      'k-collapse-open': !isCollapsed,
      'k-collapse-hide': isCollapsed,
    });
  }

  computeMarginStyles(): Record<string, string | null> {
    const verticalStrip = this.orientation === 'horizontal';
    const labelEl = this.tiSplitterBarLabel?.nativeElement;
    const labelSize = Math.max(labelEl?.clientWidth ?? 0, labelEl?.clientHeight ?? 0);
    return verticalStrip
      ? { marginBottom: `${75 + labelSize}px`, marginTop: null }
      : { marginRight: `${75 + labelSize}px`, marginLeft: null };
  }

  constructor(
    draggable: TiDraggableDirective,
    public splitterService: TiSplitterService,
    localization: LocalizationService,
  ) {
    super(draggable, splitterService, localization);
    afterNextRender(() => {
      this.marginStyles = this.computeMarginStyles();
    });
  }
}
